import {getAiAgentContext} from '../../../redux/aiAgent/aiAgentActions';
import {runAgentProposal} from './runAgentProposal';
import {holdProposal, takeHeldProposal} from './agentProposalHold';
import {getAgentChatSession, isAgentChatSessionOpen} from './agentChatSession';
import type {AiAgentProposalOptions, RunAgentProposalStatus} from './runAgentProposal';
import type {NavigateFn} from './navigateAfterExecute';
import type {AiAgentPendingProposal, AiAgentProposalOwner} from '../../../redux/aiAgent/aiAgent.type';
import type {DraftResult} from '../../aiMessageGenerator/utils/types';

export type AiAgentConfirmOptions = AiAgentProposalOptions & {
    proposalConfirmed: string;
    proposalUpdated: string;
    proposalAlreadyCreated: string;
    proposalOtherOwner: string;
};

function confirmedText(
    status: RunAgentProposalStatus,
    proposal: AiAgentPendingProposal,
    options: AiAgentConfirmOptions,
): string {
    if(status === 'repeat') {
        return options.proposalAlreadyCreated;
    }
    return proposal.update ? options.proposalUpdated : options.proposalConfirmed;
}

/**
 * Create the record the chat agreed on. The wording is the client's own: this is the only turn
 * that knows whether the record was really created, and the agent's own text was written before
 * the attempt.
 */
export async function runConfirmedProposal(
    proposal: AiAgentPendingProposal,
    owner: AiAgentProposalOwner,
    sessionId: string,
    navigate: NavigateFn,
    options: AiAgentConfirmOptions,
): Promise<DraftResult> {
    const turnSession = getAgentChatSession();
    const result = await runAgentProposal({
        proposal,
        agentText: '',
        execute: {sessionId, scope: owner.scope, eid: owner.eid, pid: owner.pid},
        navigate,
        options,
    });
    if(result.status === 'failed' && isAgentChatSessionOpen(turnSession)) {
        // Nothing was created, so the summary stays confirmable — the producer presses again
        // instead of dictating every value a second time. A summary from a chat that was left
        // is not offered again in the new one.
        holdProposal(proposal, owner);
    }
    // A failure brings its own wording. The rest ran without the agent saying anything this
    // turn, so the answer has to state what the confirmation actually did — and, under it,
    // whatever the record could not take.
    const text = result.text || confirmedText(result.status, proposal, options);
    return {...result, text: result.notice ? `${text}\n\n${result.notice}` : text};
}

/**
 * The producer answered the confirmation, so the proposal the summary turn held now runs.
 * Nothing is sent to the server first — the values were already agreed on in the chat.
 */
export async function confirmHeldProposal(
    sessionId: string,
    navigate: NavigateFn,
    options: AiAgentConfirmOptions,
): Promise<DraftResult | null> {
    // Context first: taking the proposal also drops it, and there is nothing to run it
    // against here — the summary has to stay confirmable once the owner is known again.
    const {eid, pid, scope} = getAiAgentContext();
    if(!eid && !pid) {
        return null;
    }
    const owner: AiAgentProposalOwner = {scope, eid, pid};
    const held = takeHeldProposal(owner);
    if(held.status === 'none') {
        return null;
    }
    // The producer moved to another event since the summary — creating here would put the
    // record on the wrong one. The proposal stays held for the event it belongs to.
    if(held.status === 'otherOwner') {
        return {text: options.proposalOtherOwner};
    }
    return runConfirmedProposal(held.proposal, owner, sessionId, navigate, options);
}
